import React, { useState, useMemo } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../../components/misc/Card";
import { Badge } from "../../components/calendar/Badge";
import { Button } from "../../components/misc/Button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/misc/Pagination";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import { CalendarClock, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "../../utils/classMerger";
import type { CampaignRenewalLog } from "./types";
import { cancelRenewal, getRenewalDetails, scheduleRenewal } from "./actions";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import type { ListingSyncLog, SyncLogDetails } from "../listing-sync/types";
import { cancelSync, getListingDetailsForSync } from "../listing-sync/actions";

enum ListingType {
    RentcastListing = "RentcastListing",
    MLSListing = "MLSListing",
    Promotion = "Promotion",
}

const formatCampaignType = (type?: string) => {
    switch (type) {
        case ListingType.MLSListing:
            return "MLS Listing";
        case ListingType.RentcastListing:
            return "Rentcast Listing";
        case ListingType.Promotion:
            return "Promotion";
        default:
            return type || "—";
    }
};

interface RenewalListProps {
    renewals: Array<CampaignRenewalLog | ListingSyncLog>;
    page: number;
    setPage: (n: number) => void;
    limit: number;
    total: number;
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    statusFilter?: string;
    setStatusFilter?: (s: string) => void;
    isMobile: boolean;
    stale?: boolean; // for "campaigns without renewal" mode
    sync?: boolean; // for listing‐sync mode
    fetchMetrics?: () => void;
    fetchRenewals?: (page?: number) => void;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    onSortChange?: (sortBy: string, sortDirection: "asc" | "desc") => void;
}

type NormalizedLog = {
    /** the unique log ID */
    id: string;
    /** campaignId when !sync, listingId when sync */
    parentId: string;
    /** the date to sort/display */
    date: Date;
    status: string;
    reason?: string;
    retryAttempt?: number;
    /** in seconds */
    durationSec?: number;
    campaignType?: string;
    isCampaignDataAvailable?: boolean;
    isCampaignDataAvailableInDb?: boolean;
};

type SortColumn =
    | "id"
    | "parentId"
    | "date"
    | "status"
    | "reason"
    | "retryAttempt"
    | "durationSec"
    | "campaignType";

export function RenewalList({
    renewals,
    page,
    setPage,
    limit,
    total,
    searchTerm,
    setSearchTerm,
    statusFilter = "All",
    setStatusFilter,
    isMobile,
    stale = false,
    sync = false,
    fetchMetrics,
    fetchRenewals,
    sortBy,
    sortDirection,
    onSortChange,
}: RenewalListProps) {
    // overlays state
    const [scheduleToConfirm, setScheduleToConfirm] = useState<string | null>(
      null
    );
    const [removeToConfirm, setRemoveToConfirm] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [loadingDetails, setLoadingDetails] = useState<Set<string>>(
      new Set()
    );
    const [syncDetails, setSyncDetails] = useState<Map<string, SyncLogDetails>>(
      new Map()
    );
    const [renewalDetails, setRenewalDetails] = useState<Map<string, any>>(
      new Map()
    );
 
     // 1) normalize every raw log into the same shape
     const normalized = useMemo<NormalizedLog[]>(() => {
        return renewals.map((r) => {
            const id = r._id!;
            const parentId = sync
                ? (r as ListingSyncLog).listingId!
                : (r as CampaignRenewalLog).campaignId!;
            const date = sync
                ? new Date((r as ListingSyncLog).syncDate!)
                : new Date((r as CampaignRenewalLog).renewDate!);
            const durationSec = sync
                ? (r as ListingSyncLog).syncDuration! / 1000
                : (r as CampaignRenewalLog).renewDuration! / 1000;

            return {
                id,
                parentId,
                date,
                status: r.status,
                reason: r.reason,
                retryAttempt: r.retryAttempt,
                durationSec,
                campaignType: (r as CampaignRenewalLog).campaignType,
                isCampaignDataAvailable: (r as CampaignRenewalLog).isCampaignDataAvailable ?? true,
                isCampaignDataAvailableInDb: (r as CampaignRenewalLog).isCampaignDataAvailableInDb ?? true,
            };
        });
    }, [renewals, sync]);

    // 2) mark the first log per parentId so only one "Schedule" shows
    const annotated = useMemo(() => {
        const seen = new Set<string>();
        return normalized.map((r) => {
            const canSchedule = !seen.has(r.parentId);
            seen.add(r.parentId);
            return { ...r, canSchedule };
        });
    }, [normalized]);

    const totalPages = Math.ceil(total / limit);

    const toggleRowExpansion = async (rowId: string, parentId?: string) => {
      const newExpanded = new Set(expandedRows);

      if (newExpanded.has(rowId)) {
        newExpanded.delete(rowId);
      } else {
        // Prevent expansion if data is not available
        const itemInfo = annotated.find(a => a.id === rowId);
        if (itemInfo && itemInfo.isCampaignDataAvailable === false) return;

        newExpanded.add(rowId);

        // Determine which ID to use for fetching
        const fetchId = sync ? rowId : parentId || rowId;

        // Check if we need to fetch details
        const needsFetch = sync
          ? !syncDetails.has(fetchId) // MLS sync check
          : !renewalDetails.has(fetchId); // Campaign renewal check

        if (needsFetch) {
          const newLoading = new Set(loadingDetails);
          newLoading.add(rowId); // Track loading by rowId (for UI)
          setLoadingDetails(newLoading);

          try {
            let details;

            if (sync) {
              // MLS Listing Sync flow - use rowId
              details = await getListingDetailsForSync(fetchId);
              const newSyncDetails = new Map(syncDetails);
              newSyncDetails.set(fetchId, details);
              setSyncDetails(newSyncDetails);
            } else {
              // Campaign Renewal flow - use parentId
              details = await getRenewalDetails(fetchId);
              const newRenewalDetails = new Map(renewalDetails);
              newRenewalDetails.set(fetchId, details); // Store by parentId
              setRenewalDetails(newRenewalDetails);
            }
          } catch (error) {
            console.error(
              `Failed to fetch ${sync ? "sync" : "renewal"} details:`,
              error
            );
            toast.error(
              <CustomToast
                message={`Failed to load ${
                  sync ? "listing" : "renewal"
                } details`}
                type="error"
              />,
              { autoClose: 3000 }
            );
          } finally {
            const newLoading = new Set(loadingDetails);
            newLoading.delete(rowId); // Remove loading by rowId
            setLoadingDetails(newLoading);
          }
        }
      }

      setExpandedRows(newExpanded);
    };

    // Handle column header click for sorting
    const handleSort = (column: SortColumn) => {
        if (!onSortChange) return;

        if (sortBy === column) {
            // Same column, toggle direction
            const newDirection = sortDirection === "asc" ? "desc" : "asc";
            onSortChange(column, newDirection);
        } else {
            // New column, default to ascending
            onSortChange(column, "asc");
        }
        // Reset to page 1 when sorting changes
        setPage(1);
    };

    // Render sort icon
    const renderSortIcon = (column: SortColumn) => {
        if (sortBy !== column) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4 inline ml-1" />
        ) : (
            <ChevronDown className="h-4 w-4 inline ml-1" />
        );
    };

    const handleSchedule = async (id: string) => {
        if (sync) {
            return toast.error(
                <CustomToast
                    message="Scheduling syncs is not supported."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
        try {
            await scheduleRenewal(id);
            toast.success(
                <CustomToast
                    message="Renewal task scheduled successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
            fetchMetrics?.();
            fetchRenewals?.(page);
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to schedule renewal. Please try again."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        }
    };

    const handleRemove = async (id: string) => {
        try {
            sync ? await cancelSync(id) : await cancelRenewal(id);
            toast.success(
                <CustomToast
                    message={
                        sync ? "Listing sync removed." : "Renewal task removed."
                    }
                    type="success"
                />,
                {
                    autoClose: 3000,
                }
            );
            fetchMetrics?.();
            fetchRenewals?.(page);
        } catch {
            toast.error(
                <CustomToast
                    message={
                        sync
                            ? "Failed to remove sync."
                            : "Failed to remove renewal."
                    }
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    const badgeVariant = (status: string) =>
        ["New", "Scheduled"].includes(status)
            ? "secondary"
            : status === "Failed"
            ? "destructive"
            : status === "Completed"
            ? "success"
            : "default";

    const badgeClass = (status: string) => {
        switch (status) {
            case "Completed":
                return "bg-green-100 text-green-800";
            case "Failed":
                return "bg-red-100 text-red-800";
            case "InProgress":
                return "bg-yellow-100 text-yellow-800";
            case "New":
                return "bg-blue-100 text-blue-800";
            case "Cancelled":
                return "bg-gray-100 text-gray-800";
            default:
                return "";
        }
    };

    return (
        <Card className={cn()}>
            <CardHeader>
                <CardTitle>
                    {stale
                        ? "Campaigns Without Renewal Tasks"
                        : sync
                        ? "Listing Syncs"
                        : "Campaign Renewals"}
                </CardTitle>
                {/* filters */}
                <div
                    className={`flex ${
                        isMobile ? "flex-col" : "flex-row"
                    } pt-4 gap-x-10`}
                >
                    <input
                        type="text"
                        placeholder={
                            stale
                                ? "Search by Campaign ID"
                                : sync
                                ? "Search by Sync or Listing ID"
                                : "Search by Renewal or Campaign ID"
                        }
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        className="border rounded px-2 py-1 flex-1"
                    />
                    {!stale && (
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter?.(e.target.value);
                                setPage(1);
                            }}
                            className="border rounded px-2 py-1 mt-2 sm:mt-0 flex-1"
                        >
                            {[
                                "All",
                                "New",
                                "Scheduled",
                                "InProgress",
                                "Completed",
                                "Failed",
                                "Cancelled",
                            ].map((s) => (
                                <option key={s} value={s}>
                                    {s === "All" ? "All Statuses" : s}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            {!stale && (
                                <TableHead
                                    className={
                                        onSortChange
                                            ? "cursor-pointer hover:bg-gray-50"
                                            : ""
                                    }
                                    onClick={
                                        onSortChange
                                            ? () => handleSort("id")
                                            : undefined
                                    }
                                >
                                    CloudTask ID
                                    {renderSortIcon("id")}
                                </TableHead>
                            )}
                            <TableHead
                                className={
                                    onSortChange
                                        ? "cursor-pointer hover:bg-gray-50"
                                        : ""
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("parentId")
                                        : undefined
                                }
                            >
                                {sync ? "Listing ID" : "Campaign ID"}
                                {renderSortIcon("parentId")}
                            </TableHead>
                            {!sync && (
                                <TableHead
                                    className={
                                        onSortChange
                                            ? "cursor-pointer hover:bg-gray-50"
                                            : ""
                                    }
                                    onClick={
                                        onSortChange
                                            ? () => handleSort("campaignType")
                                            : undefined
                                    }
                                >
                                    Campaign Type
                                    {renderSortIcon("campaignType")}
                                </TableHead>
                            )}
                            <TableHead
                                className={
                                    onSortChange
                                        ? "cursor-pointer hover:bg-gray-50"
                                        : ""
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("date")
                                        : undefined
                                }
                            >
                                {sync ? "Sync Date" : "Renewal Date"}
                                {renderSortIcon("date")}
                            </TableHead>
                            {stale && <TableHead>End Date</TableHead>}
                            <TableHead
                                className={
                                    onSortChange
                                        ? "cursor-pointer hover:bg-gray-50"
                                        : ""
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("status")
                                        : undefined
                                }
                            >
                                Status
                                {renderSortIcon("status")}
                            </TableHead>
                            <TableHead
                                className={
                                    onSortChange
                                        ? "text-center cursor-pointer hover:bg-gray-50"
                                        : "text-center"
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("reason")
                                        : undefined
                                }
                            >
                                Reason
                                {renderSortIcon("reason")}
                            </TableHead>
                            <TableHead
                                className={
                                    onSortChange
                                        ? "text-center cursor-pointer hover:bg-gray-50"
                                        : "text-center"
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("retryAttempt")
                                        : undefined
                                }
                            >
                                Retries
                                {renderSortIcon("retryAttempt")}
                            </TableHead>
                            <TableHead
                                className={
                                    onSortChange
                                        ? "text-center cursor-pointer hover:bg-gray-50"
                                        : "text-center"
                                }
                                onClick={
                                    onSortChange
                                        ? () => handleSort("durationSec")
                                        : undefined
                                }
                            >
                                {sync ? "Sync" : "Renewal"} Duration
                                {renderSortIcon("durationSec")}
                            </TableHead>
                            <TableHead className="text-center">
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {annotated.length > 0 ? (
                            annotated.map((r) => {
                                const isExpanded = expandedRows.has(r.id);
                                const isLoading = loadingDetails.has(r.id);

                                const detailsKey = sync ? r.id : r.parentId;
                                const details = sync 
                                    ? syncDetails.get(detailsKey) 
                                    : renewalDetails.get(detailsKey);

                                return (
                                  <React.Fragment key={r.id}>
                                      <TableRow
                                        className={cn(
                                          r.isCampaignDataAvailable !== false && "cursor-pointer hover:bg-gray-50"
                                        )}
                                        onClick={() =>
                                          r.isCampaignDataAvailable !== false && toggleRowExpansion(r.id, r.parentId)
                                        }
                                      >
                                      {!stale && (
                                        <TableCell className="font-medium">
                                          <div className="flex items-center gap-2">
                                            {r.id}
                                            {r.isCampaignDataAvailable !== false && (
                                              isExpanded ? (
                                                <ChevronUp className="h-4 w-4" />
                                              ) : (
                                                <ChevronDown className="h-4 w-4" />
                                              )
                                            )}
                                          </div>
                                        </TableCell>
                                      )}
                                      <TableCell className="font-medium">
                                        {r.parentId}
                                      </TableCell>
                                      {!sync && (
                                        <TableCell>
                                          {formatCampaignType(r.campaignType)}
                                        </TableCell>
                                      )}
                                      <TableCell>
                                        {r.date.toLocaleString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          hour12: true,
                                        })}
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={badgeVariant(r.status)}
                                          className={badgeClass(r.status)}
                                        >
                                          {r.status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {r.reason || "—"}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {r.retryAttempt ?? 0}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {r.durationSec != null
                                          ? `${r.durationSec}s`
                                          : "—"}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <div className="flex justify-end gap-2">
                                          {!sync && (
                                            <Button
                                              size="sm"
                                              onClick={() =>
                                                setScheduleToConfirm(r.parentId)
                                              }
                                              disabled={
                                                !r.canSchedule ||
                                                r.isCampaignDataAvailable === false ||
                                                r.isCampaignDataAvailableInDb === false ||
                                                [
                                                  "Completed",
                                                  "InProgress",
                                                ].includes(r.status)
                                              }
                                            >
                                              <CalendarClock className="h-4 w-4 mr-1" />
                                              Schedule
                                            </Button>
                                          )}
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation(); // Prevent row expansion when clicking remove
                                              setRemoveToConfirm(r.id);
                                            }}
                                            disabled={[
                                              "Completed",
                                              "Cancelled",
                                              "ACTIVE",
                                            ].includes(r.status)}
                                          >
                                            <Trash2 className="h-4 w-4 mr-1" />
                                            Remove
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>

                                    {/* Expanded row content for sync logs and campaign renewal */}
                                    {isExpanded && (
                                      <TableRow>
                                        <TableCell
                                          colSpan={stale ? 7 : 8}
                                          className="bg-gray-50 p-6"
                                        >
                                          {isLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                              <span className="ml-3 text-gray-600">
                                                Loading {r.campaignType === ListingType.Promotion ? "promotion" : "listing"} details...
                                              </span>
                                            </div>
                                          ) : details ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                              {/* 1. MLS Listing OR Rentcast Listing (Property Info) */}
                                              {(details.listingType === ListingType.MLSListing || details.listingType === ListingType.RentcastListing || (!details.listingType && details.listing)) && details.listing && (
                                                <>
                                                  <div>
                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">
                                                      Property Information
                                                    </h4>
                                                    <div className="space-y-2">
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Address:</span>
                                                        <span className="font-medium">
                                                          {details.listingType === ListingType.RentcastListing 
                                                            ? details.listing.formattedAddress 
                                                            : [
                                                                details.listing.UnparsedAddress,
                                                                details.listing.PostalCity,
                                                                [details.listing.StateOrProvince, details.listing.PostalCode].filter(Boolean).join(" "),
                                                              ].filter(Boolean).join(", ")
                                                          }
                                                        </span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">{details.listingType === ListingType.RentcastListing ? "Property Type:" : "MLS Number:"}</span>
                                                        <span className="font-medium">
                                                          {details.listingType === ListingType.RentcastListing 
                                                            ? details.listing.propertyType || "N/A"
                                                            : details.listing.ListingId || "N/A"
                                                          }
                                                        </span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">{details.listingType === ListingType.RentcastListing ? "Bedrooms:" : "MLS System:"}</span>
                                                        <span className="font-medium">
                                                          {details.listingType === ListingType.RentcastListing 
                                                            ? details.listing.bedrooms || "N/A"
                                                            : details.listing.ListAOR || "N/A"
                                                          }
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">
                                                      Realtor Contact
                                                    </h4>
                                                    <div className="space-y-2">
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Name:</span>
                                                        <span className="font-medium">{details.listing.ListAgentFullName || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Office:</span>
                                                        <span className="font-medium">{details.listing.ListOfficeName || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Email:</span>
                                                        <span className="font-medium">{details.listing.ListAgentEmail || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Mobile:</span>
                                                        <span className="font-medium">{details.listing.ListAgentMobilePhone || "N/A"}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </>
                                              )}

                                              {/* 2. Promotion details */}
                                              {details.listingType === ListingType.Promotion && details.promotion && (
                                                <>
                                                  <div>
                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">
                                                      Promotion Information
                                                    </h4>
                                                    <div className="space-y-2">
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Promotion Types:</span>
                                                        <span className="font-medium">
                                                          {details.promotion.types?.join(", ") || "N/A"}
                                                        </span>
                                                      </div>
                                                      {details.promotion.campaign?.[0] && (
                                                        <>
                                                          <div className="flex space-x-2">
                                                            <span className="text-gray-600">Campaign Status:</span>
                                                            <span className="font-medium">{details.promotion.campaign[0].status || "N/A"}</span>
                                                          </div>
                                                          <div className="flex space-x-2">
                                                            <span className="text-gray-600">Duration:</span>
                                                            <span className="font-medium">
                                                              {new Date(details.promotion.campaign[0].startDate).toLocaleDateString()} - {new Date(details.promotion.campaign[0].endDate).toLocaleDateString()}
                                                            </span>
                                                          </div>
                                                          <div className="flex space-x-2">
                                                            <span className="text-gray-600">Auto Renew:</span>
                                                            <span className="font-medium">{details.promotion.campaign[0].autoRenew ? "Yes" : "No"}</span>
                                                          </div>
                                                          <div className="flex space-x-2">
                                                            <span className="text-gray-600">Platforms:</span>
                                                            <span className="font-medium">
                                                              {details.promotion.campaign[0].platforms?.map((p: any) => p.name).join(", ") || "N/A"}
                                                            </span>
                                                          </div>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* MB-1388: owning user, mirroring the listing rows' Realtor Contact */}
                                                  <div>
                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">
                                                      User
                                                    </h4>
                                                    <div className="space-y-2">
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Name:</span>
                                                        <span className="font-medium">{details.promotion.ownerName || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Email:</span>
                                                        <span className="font-medium">{details.promotion.ownerEmail || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Mobile:</span>
                                                        <span className="font-medium">{details.promotion.ownerPhone || "N/A"}</span>
                                                      </div>
                                                      <div className="flex space-x-2">
                                                        <span className="text-gray-600">Company:</span>
                                                        <span className="font-medium">{details.promotion.ownerCompany || "N/A"}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </>
                                              )}

                                              {/* Changes for completed syncs */}
                                              {r.status === "Completed" && details?.syncLog?.updates && details.syncLog.updates.length > 0 && (
                                                <div className="md:col-span-2 mt-4">
                                                  <h4 className="font-semibold text-lg mb-3 text-gray-800">Sync Changes</h4>
                                                  <div className="bg-white p-3 rounded border max-h-32 overflow-y-auto">
                                                    {details.syncLog.updates.map((update: string, index: number) => (
                                                      <div key={index} className="text-sm text-gray-700 mb-1">{update}</div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="text-center py-8 text-gray-500">
                                              <p>Unable to load {r.campaignType === ListingType.Promotion ? "promotion" : "listing"} details</p>
                                            </div>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={sync ? 8 : 9}
                                    className="text-center py-6 text-muted-foreground"
                                >
                                    No tasks found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
            {totalPages > 1 && (
                <CardFooter>
                    <Pagination className="w-full">
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() =>
                                        setPage(Math.max(page - 1, 1))
                                    }
                                    className={cn(
                                        page === 1 &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                            {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                    let pageNum: number | null = null;
                                    if (i === 0) pageNum = 1;
                                    else if (i === 4) pageNum = totalPages;
                                    else if (totalPages <= 5) pageNum = i + 1;
                                    else if (page <= 3) pageNum = i + 1;
                                    else if (page >= totalPages - 2)
                                        pageNum = totalPages - 4 + i;
                                    else pageNum = page - 1 + i;
                                    if (!pageNum) {
                                        return (
                                            <PaginationItem key={`e-${i}`}>
                                                <PaginationEllipsis />
                                            </PaginationItem>
                                        );
                                    }
                                    return (
                                        <PaginationItem key={pageNum}>
                                            <PaginationLink
                                                isActive={page === pageNum}
                                                onClick={() =>
                                                    setPage(pageNum!)
                                                }
                                            >
                                                {pageNum}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                            )}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() =>
                                        setPage(Math.min(page + 1, totalPages))
                                    }
                                    className={cn(
                                        page === totalPages &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </CardFooter>
            )}
            <ReconfirmOverlay
                isOpen={Boolean(scheduleToConfirm)}
                title="Schedule Renewal?"
                message="Do you want to schedule this renewal task?"
                messageTwo={`ID: ${scheduleToConfirm}`}
                confirmText="Schedule"
                onClose={() => setScheduleToConfirm(null)}
                onConfirm={() => {
                    handleSchedule(scheduleToConfirm!);
                    setScheduleToConfirm(null);
                }}
            />
            <ReconfirmOverlay
                isOpen={Boolean(removeToConfirm)}
                title="Remove Task?"
                message="This action cannot be undone."
                messageTwo={`ID: ${removeToConfirm}`}
                confirmText="Remove"
                onClose={() => setRemoveToConfirm(null)}
                onConfirm={() => {
                    handleRemove(removeToConfirm!);
                    setRemoveToConfirm(null);
                }}
            />
        </Card>
    );
}
